// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { alphaSort } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import type { User } from '../../auth/types'
import withUser from '../../auth/withUser'
import { i18n } from '../../index/index'
import ImageOverlay from '../../weeklyInfo/ImageOverlay/ImageOverlay'
import Images from '../../weeklyInfo/components/Images'
import Links from '../../weeklyInfo/components/Links'
import MonthlyInfoPage from '../MonthlyInfoPage'
import RequiresAction from '../RequiresAction'
import SalesAndHours from '../SalesAndHours'
import css from '../monthlyInfoPage.css'
import { fetchMonthlyInfo } from '../monthlyInfoQueries'
import type { MonthlyInfoMarket, MonthlyInfoVariables } from '../monthlyInfoTypes'
import MarketSalesAndGrossProfit from './MarketSalesAndGrossProfit'
import MarketWastages from './MarketWastages'
type MarketQuestionRowProps = {
  stationId: string
  date: string
  data: MonthlyInfoMarket
  canEdit: boolean
  refetch: (...args: Array<any>) => any
}

const MarketQuestionRow = ({ stationId, date, data, canEdit, refetch }: MarketQuestionRowProps) => (
  <TableRow>
    <TableCell className={cx(css.tableCell)}>{data.description}</TableCell>
    <TableCell className={cx(css.tableCell, css.actionCell)}>
      {canEdit && (
        <RequiresAction
          stationId={stationId}
          date={date}
          monthlyInfoId={data.id}
          subject='Market'
          isActionRequired={data.isActionRequired}
          refetch={refetch}
        />
      )}
    </TableCell>
  </TableRow>
)

type MarketQuestionProps = {
  stationId: string
  date: string
}

const MarketQuestion = ({ stationId, date }: MarketQuestionProps) => (
  <Query<Array<MonthlyInfoMarket>, MonthlyInfoVariables>
    query={fetchMonthlyInfo}
    variables={{
      stationId,
      date,
      type: 'Market',
    }}
  >
    {({ data, error, loading, refetch }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.marketQuestion}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('monthlyInfo.title.marketQuestions')}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table size='small' className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={css.tableHeading}>{i18n.t('monthlyInfo.heading.taskName')}</TableCell>
                      <TableCell className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('monthlyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alphaSort('description', get(data, 'monthlyInfo', [])).map((item) => (
                      <MarketQuestionRow
                        stationId={stationId}
                        date={date}
                        data={item}
                        canEdit={hasPermission}
                        key={item.id}
                        refetch={refetch}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

type MarketPageProps = {
  user: User
}

@withUser
class MarketPage extends React.PureComponent<MarketPageProps> {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <MonthlyInfoPage className={css.component} subject='Market'>
        {({ stationId, date, dateFormat, onDateChange, companyId }) => (
          <div className={css.content}>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <SalesAndHours stationId={stationId} date={date} showOnly='Market' />

            <MarketSalesAndGrossProfit stationId={stationId} date={date} />

            <MarketWastages stationId={stationId} date={date} />

            <MarketQuestion stationId={stationId} date={date} />

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'monthlyinfo_market'} />

            <Links stationId={stationId} linkType={'monthlyinfo_market'} companyId={companyId} />
          </div>
        )}
      </MonthlyInfoPage>
    )
  }
}

export default MarketPage
